/**
 * The deposit-match promotion, on the screen that runs the game.
 *
 * It existed as a model with every gate worked out and no way to see any of it
 * outside the Django admin -- so the setting that decides how much bonus
 * credit BR1 hands out ran where the people running the game could not look at
 * it.
 *
 * FOUR GATES, NOT A SWITCH. Enabled, a start, an end, and a weekly schedule.
 * All four must pass, so "on" and "paying" are different states and the
 * difference is worth an afternoon when nobody can tell them apart. The server
 * already names which gate is shut (`inactive_reason`), and this prints that
 * sentence rather than inferring anything from the toggle.
 *
 * THE EXAMPLE IS THE POINT. A ratio, a floor and four caps do not add up to a
 * number in anybody's head. "Deposit $50, they get $25" is the thing being
 * decided, and it is computed by the promotion's own `match_for` on the server
 * rather than re-derived here, so the line cannot drift from what a real
 * deposit would actually pay.
 *
 * Bonus funds are spendable on spawn fees and never withdrawable, which is why
 * this is safe to leave switched on and why nothing here needs an executive.
 */
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import { csrftoken } from '~/api'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { twMerge } from 'tailwind-merge'

export type DepositMatch = {
    enabled: boolean
    match_ratio: number
    min_deposit: number
    max_match_per_deposit: number | null
    max_per_user_daily: number | null
    max_per_user_monthly: number | null
    max_per_user_total: number | null
    starts_at: string | null
    ends_at: string | null
    active_weekdays: number[]
    schedule_label: string
    inactive_reason: string | null
    updated_at: string | null
    example: { deposit: number; bonus: number }
}

const URL = '/team-tools/api/deposit-match'

/** Monday first, matching `Weekday` on the model and Python's `weekday()`. */
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const INPUT =
    'w-28 rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/50'

const Row = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
    <label
        className='flex flex-row flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/10 py-2 last:border-0'
        title={hint}
    >
        <span className='w-52 shrink-0 text-[11px] uppercase tracking-widest text-white/60'>{label}</span>
        {children}
    </label>
)

/** A cap that can be "no limit". Empty means unlimited, and says so, because
 *  an empty money box otherwise reads as zero. */
const Cap = ({
    value,
    onChange,
}: {
    value: number | null
    onChange: (next: number | null) => void
}) => (
    <>
        <input
            type='number'
            step='0.01'
            min='0'
            value={value ?? ''}
            placeholder='none'
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className={INPUT}
        />
        <span className='text-[11px] text-white/45'>
            {value === null ? 'no limit' : 'USDC of bonus'}
        </span>
    </>
)

export const DepositMatchCard = () => {
    const { addNotification } = useNotification()
    const [data, setData] = useState<DepositMatch | null>(null)
    const [draft, setDraft] = useState<DepositMatch | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [open, setOpen] = useState(false)

    const load = useCallback(async () => {
        try {
            const res = await fetch(URL, { headers: { Accept: 'application/json' } })
            if (!res.ok) throw new Error(`Could not read the promotion (${res.status})`)
            const json = await res.json()
            setData(json)
            setDraft(json)
        } catch (err: any) {
            setError(err.message)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    if (error && !data)
        return <div className='rounded border border-red-500/40 p-3 text-red-400'>{error}</div>
    if (!draft || !data)
        return (
            <div className='rounded border border-white/15 p-3 text-white/40'>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading deposit match…
            </div>
        )

    const set = (patch: Partial<DepositMatch>) => setDraft({ ...draft, ...patch })
    const dirty = JSON.stringify(draft) !== JSON.stringify(data)

    const saveNow = async (patch?: Partial<DepositMatch>) => {
        setSaving(true)
        setError(null)
        try {
            const res = await fetch(URL, {
                method: 'POST',
                // Without the token Django refuses the write with an HTML
                // error page, which arrives here as "Unexpected token '<'".
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken() },
                body: JSON.stringify({ ...draft, ...patch }),
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json.error ?? 'The promotion was refused.')
            setData(json)
            setDraft(json)
            addNotification('Deposit match saved', 'success')
        } catch (err: any) {
            setError(err.message)
        } finally {
            setSaving(false)
        }
    }

    const toggleDay = (day: number) =>
        set({
            active_weekdays: draft.active_weekdays.includes(day)
                ? draft.active_weekdays.filter((d) => d !== day)
                : [...draft.active_weekdays, day].sort(),
        })

    return (
        <div className='flex flex-col gap-2 rounded border border-white/20 p-4 text-xs'>
            <div className='flex flex-row flex-wrap items-center gap-3'>
                <span className='text-sm font-bold uppercase tracking-wide'>Matching deposits</span>

                {/* The switch is on the face of the card, not behind "edit".
                    It was the one control somebody opens this section to find,
                    and it was the only one you had to go looking for. Saves on
                    the click rather than waiting for the Save button: turning a
                    promotion on is the decision, and a toggle that needs
                    confirming reads as not having worked. */}
                <button
                    type='button'
                    disabled={saving}
                    onClick={() => saveNow({ enabled: !draft.enabled })}
                    className={twMerge(
                        'rounded border px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest disabled:opacity-40',
                        draft.enabled
                            ? 'border-green-500/60 bg-green-500/10 text-green-400'
                            : 'border-white/25 text-white/50 hover:border-white/50'
                    )}
                >
                    {draft.enabled ? 'on' : 'off'}
                </button>

                {/* On and paying are different states, and the server is the
                    one that knows which. */}
                {data.inactive_reason ? (
                    <span className='rounded border border-amber-400/40 px-2 py-0.5 text-amber-300'>
                        not paying &mdash; {data.inactive_reason}
                    </span>
                ) : (
                    <span className='rounded border border-green-500/40 px-2 py-0.5 text-green-400'>
                        paying now
                    </span>
                )}

                <span className='ml-auto text-white/45'>
                    Deposit {data.example.deposit.toFixed(0)} USDC &rarr; they get{' '}
                    <b className='text-white'>{data.example.bonus.toFixed(2)}</b> bonus
                </span>

                <button
                    type='button'
                    onClick={() => setOpen((v) => !v)}
                    className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                >
                    {open ? 'hide' : 'edit'}
                </button>
            </div>

            <p className='text-white/50'>
                Bonus funds are spendable on spawn fees only &mdash; never withdrawable, and not valid in
                the store. No edit here can open a path to a payout.
            </p>

            {open && (
                <div className='flex flex-col pt-1'>
                    <Row
                        label='Match ratio'
                        hint='Bonus granted per 1 USDC deposited. 1 is a full match, 0.5 a half match.'
                    >
                        <input
                            type='number'
                            step='0.05'
                            min='0'
                            value={draft.match_ratio}
                            onChange={(e) => set({ match_ratio: Number(e.target.value) })}
                            className={INPUT}
                        />
                        <span className='text-[11px] text-white/45'>
                            per 1 USDC &mdash; {Math.round(draft.match_ratio * 100)}% match
                        </span>
                    </Row>

                    <Row label='Smallest deposit' hint='Deposits below this are not matched at all.'>
                        <input
                            type='number'
                            step='0.01'
                            min='0'
                            value={draft.min_deposit}
                            onChange={(e) => set({ min_deposit: Number(e.target.value) })}
                            className={INPUT}
                        />
                        <span className='text-[11px] text-white/45'>
                            {draft.min_deposit > 0 ? 'USDC' : 'every deposit is matched'}
                        </span>
                    </Row>

                    <Row label='Most per deposit'>
                        <Cap
                            value={draft.max_match_per_deposit}
                            onChange={(max_match_per_deposit) => set({ max_match_per_deposit })}
                        />
                    </Row>
                    <Row label='Most per player, a day'>
                        <Cap
                            value={draft.max_per_user_daily}
                            onChange={(max_per_user_daily) => set({ max_per_user_daily })}
                        />
                    </Row>
                    <Row label='Most per player, a month'>
                        <Cap
                            value={draft.max_per_user_monthly}
                            onChange={(max_per_user_monthly) => set({ max_per_user_monthly })}
                        />
                    </Row>
                    <Row label='Most per player, ever'>
                        <Cap
                            value={draft.max_per_user_total}
                            onChange={(max_per_user_total) => set({ max_per_user_total })}
                        />
                    </Row>

                    <Row label='Runs on' hint='All days off means every day. Read in UTC.'>
                        <span className='flex flex-row flex-wrap gap-1'>
                            {DAYS.map((name, day) => (
                                <button
                                    key={name}
                                    type='button'
                                    onClick={() => toggleDay(day)}
                                    className={twMerge(
                                        'rounded border px-2 py-0.5 text-[10px] uppercase tracking-widest',
                                        draft.active_weekdays.includes(day)
                                            ? 'border-white/60 bg-white/10 text-white'
                                            : 'border-white/15 text-white/40 hover:border-white/35'
                                    )}
                                >
                                    {name}
                                </button>
                            ))}
                            <span className='self-center pl-1 text-[11px] text-white/45'>
                                {draft.active_weekdays.length === 0 ? 'every day' : 'UTC'}
                            </span>
                        </span>
                    </Row>

                    {error && <span className='pt-2 text-red-400'>{error}</span>}

                    <div className='flex flex-row items-center gap-3 pt-3'>
                        <button
                            type='button'
                            onClick={() => saveNow()}
                            disabled={!dirty || saving}
                            className='rounded border border-white/30 px-3 py-1 text-[10px] uppercase tracking-widest hover:border-white/60 disabled:opacity-30'
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                        {dirty && (
                            <button
                                type='button'
                                onClick={() => setDraft(data)}
                                className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                            >
                                discard
                            </button>
                        )}
                        <span className='ml-auto text-white/35'>
                            {data.updated_at
                                ? `last changed ${new Date(data.updated_at).toLocaleString()}`
                                : ''}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}
